import { CoreLayoutView, CoreTextView, ORIENTATION_ENUM, TCoreTextOptions, useThemeHook } from '@core/widgets';
import { useApp, useBehavior, useButton, useTriggerAction } from '@libs/core';
import { AssetsSvgMap, TBaseCalculatorData, TItem, TWidget } from '@libs/types';
import React from 'react';
import { SemanticColor, semanticColorsV1 as semanticColors } from '../../../theme/colors';
import { EWidgetVersion } from '../../../types/enums';
import { TextStyle } from '../../../types/styles';
import { ButtonRowView } from '../../Buttons/ButtonRowView';
import { VisualView } from '../../Image/VisualView';
import { SEARCH_ENABLED_ITEMS_COUNT_DEFAULT } from '../../List/utils/ListConstant';
import { FlagIcon } from '../../UI/FlagIcon/FlagIcon';
import { PopupComponent } from '../../UI/Popup/PopupComponent';
import { ListCurrency } from '../ListCurrency';
import { ActionNameEnum, CurrencyDropdownEnum, TServerCalculationFunc } from '../utils/types';

type TCurrencyDropdownPalette = {
    calculatorInputCurrencyText?: TCoreTextOptions;
} & TCoreTextOptions;

type TCurrencyDropdownProps = {
    widget: TWidget<TBaseCalculatorData>;
    value: string;
    flagName?: string;
    name: CurrencyDropdownEnum;
    header: string;
    noFoundText?: string;
    currencyText?: string;
    buttonChangeTransferOptionLabel?: string;
    searchPlaceholder?: string;
    index: number;
    items: TItem[];
    isFrom?: boolean;
    isTopUp?: boolean;
    isSingleCurrency: boolean;
    onServerCalculation: TServerCalculationFunc;
    searchEnabledItemsCount?: number;
    parentStyleNames?: string[];
};

const CurrencyDropdownV1 = React.memo(
    ({
        widget,
        value,
        flagName,
        name,
        header,
        noFoundText,
        currencyText,
        index,
        items,
        isFrom,
        isTopUp,
        isSingleCurrency,
        onServerCalculation,
        searchPlaceholder,
        parentStyleNames,
        buttonChangeTransferOptionLabel = '',
        searchEnabledItemsCount = SEARCH_ENABLED_ITEMS_COUNT_DEFAULT,
    }: TCurrencyDropdownProps) => {
        const { setPopup } = useApp();
        const version = EWidgetVersion.V1;
        const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
        const { isDisabled } = useBehavior(widget);
        const { handleClickByActionName } = useButton(widget, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });
        const { palette } = useThemeHook<TCurrencyDropdownPalette>(widget);
        const currencyTextPalette = palette?.calculatorInputCurrencyText ?? {};
        const isShowSearchFunctionality = items.length >= searchEnabledItemsCount;
        const button =
            !isTopUp && !isFrom
                ? [
                      <ButtonRowView
                          version={version}
                          key="ChangeTransferOption"
                          label={buttonChangeTransferOptionLabel}
                          iconName="ic_arrows_exchange"
                          parentStyleNames={parentStyleNames}
                          onClick={() => {
                              setPopup(null);
                              handleClickByActionName(ActionNameEnum.onChangeTransferOptions);
                          }}
                      />,
                  ]
                : [];

        const handleClick = () => {
            setPopup(
                <PopupComponent
                    fixedHeight={isShowSearchFunctionality}
                    withoutSeparator={isShowSearchFunctionality}
                    header={
                        !isShowSearchFunctionality && (
                            <CoreTextView
                                text={header}
                                styleName="calculatorCurrencyListTitle"
                                parentStyleNames={parentStyleNames}
                                styleProps={{ ...TextStyle.main, main: 'var(--black)', textAlign: 'center' }}
                            />
                        )
                    }
                    internalContent={
                        <ListCurrency
                            version={version}
                            header={header}
                            noFoundText={noFoundText}
                            currencyText={currencyText}
                            isShowSearchFunctionality={isShowSearchFunctionality}
                            searchPlaceholder={searchPlaceholder}
                            index={index}
                            items={items}
                            parentStyleNames={parentStyleNames}
                            handleClick={async (value) => {
                                setPopup(null);
                                await onServerCalculation(value, name, isFrom);
                            }}
                        />
                    }
                    externalContent={button}
                />,
            );
        };
        const borderStyleProps = isSingleCurrency ? {} : { borderRadius: 6, borderStroke: 1, borderColor: 'var(--grey_16_tr)' };
        return (
            <CoreLayoutView
                uid={`calculator_currency_dropdown_${isFrom ?? ''}`}
                styleName={
                    // `calculatorInputSingleCurrencyText` styles the single-currency ROW despite the "Text" suffix (theme key is misnamed, to be renamed).
                    isSingleCurrency ? 'calculatorInputSingleCurrencyText' : 'calculatorInputCurrencyRow'
                }
                parentStyleNames={parentStyleNames}
                onClick={isSingleCurrency ? undefined : handleClick}
                styleProps={{
                    orientation: ORIENTATION_ENUM.HORIZONTAL,
                    lineY: 'center',
                    width: 'auto',
                    padding: { top: 8, right: 6, bottom: 8, left: 8 },
                    margin: { top: 4 },
                    ...borderStyleProps,
                }}
            >
                <FlagIcon name={flagName ?? ''} styleName="calculatorInputFlag" parentStyleNames={parentStyleNames} />
                <CoreTextView
                    as="span"
                    text={value?.toUpperCase()}
                    styleName="calculatorInputCurrencyIso"
                    parentStyleNames={parentStyleNames}
                    styleProps={{ ...TextStyle.main, main: semanticColors[SemanticColor.TextPrimary], margin: { left: 4 }, ...currencyTextPalette }}
                />
                {!isSingleCurrency && (
                    <VisualView
                        version={version}
                        iconName={AssetsSvgMap.ic_chevron_down_small}
                        defaultIconName={AssetsSvgMap.ic_x}
                        iconHierarchy="primary"
                        iconWidth={16}
                        iconHeight={16}
                        parentStyleNames={parentStyleNames}
                        styleName="calculatorInputChevron"
                    />
                )}
            </CoreLayoutView>
        );
    },
);

export { CurrencyDropdownV1 };
